// Admin > Surveys > Results: response figures, a chart per question, NPS, per-vendor breakdown,
// individual responses, invitations with their delivery log, and CSV export.
import React, { useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Stack, Typography, Paper, Grid, Tabs, Tab, Box, Chip, Table, TableHead, TableRow,
  TableCell, TableBody, TextField, MenuItem, Button, Tooltip, Rating, LinearProgress, Alert, Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { SURVEY_RESULTS_QUERY, SURVEY_INVITATIONS_QUERY, SURVEY_RESPONSES_CSV_QUERY } from '../graphql/queries';
import PersonSurveys, { VIA_LABEL } from './PersonSurveys';

const BAR = '#7B1FA2';           // one series: one hue
const NPS = { detractors: '#C8651B', passives: '#8C8F94', promoters: '#2F6DB3' }; // diverging pair + neutral midpoint (validated)
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const pct = (n, d) => (d ? Math.round((100 * n) / d) : 0);
const STATUS_COLOR = { completed: 'success', sent: 'info', opened: 'info', held: 'warning', pending: 'default', expired: 'default', failed: 'error' };

function Tile({ label, value, sub }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h4" fontWeight={700}>{value ?? '—'}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

// Horizontal bars, one hue; label, bar, count and share; hover for the exact figures
function Bars({ rows }) {
  const total = rows.reduce((t, r) => t + r.count, 0);
  const max = Math.max(1, ...rows.map(r => r.count));
  return (
    <Stack spacing={0.75}>
      {rows.map(r => (
        <Tooltip key={r.label} placement="top" title={`${r.label}: ${r.count} response${r.count === 1 ? '' : 's'} (${pct(r.count, total)}%)`}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ cursor: 'default' }}>
            <Typography variant="body2" sx={{ width: 180, flexShrink: 0 }} noWrap title={r.label}>{r.label}</Typography>
            <Box sx={{ flex: 1, height: 18, bgcolor: '#F1ECF4', borderRadius: '0 4px 4px 0' }}>
              <Box sx={{ width: `${(100 * r.count) / max}%`, minWidth: r.count ? 4 : 0, height: '100%', bgcolor: BAR, borderRadius: '0 4px 4px 0' }} />
            </Box>
            <Typography variant="body2" sx={{ width: 90, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.count} · {pct(r.count, total)}%</Typography>
          </Stack>
        </Tooltip>
      ))}
    </Stack>
  );
}

function NpsBar({ q }) {
  const total = (q.promoters || 0) + (q.passives || 0) + (q.detractors || 0);
  if (!total) return null;
  const parts = [['Detractors (0–6)', q.detractors, NPS.detractors], ['Passives (7–8)', q.passives, NPS.passives], ['Promoters (9–10)', q.promoters, NPS.promoters]];
  return (
    <Box mb={1.5}>
      <Stack direction="row" alignItems="baseline" spacing={1} mb={1}>
        <Typography variant="h4" fontWeight={700}>{q.npsScore > 0 ? `+${q.npsScore}` : q.npsScore}</Typography>
        <Typography variant="body2" color="text.secondary">Net Promoter Score (promoters − detractors, % of {total})</Typography>
      </Stack>
      <Stack direction="row" sx={{ height: 22, gap: '2px' }}>
        {parts.filter(p => p[1]).map(([l, n, c]) => (
          <Tooltip key={l} title={`${l}: ${n} (${pct(n, total)}%)`}><Box sx={{ width: `${pct(n, total)}%`, bgcolor: c, borderRadius: '4px' }} /></Tooltip>
        ))}
      </Stack>
      <Stack direction="row" spacing={2} mt={0.75} flexWrap="wrap" useFlexGap>
        {parts.map(([l, n, c]) => (
          <Stack key={l} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 12, height: 12, bgcolor: c, borderRadius: '3px' }} />
            <Typography variant="caption">{l}: <b>{n}</b> ({pct(n, total)}%)</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function QuestionCard({ q, index }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Typography fontWeight={700}>{index}. {q.label}</Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>{q.answered} answer{q.answered === 1 ? '' : 's'}</Typography>
      {!q.answered && <Typography variant="body2" color="text.secondary">No answers yet.</Typography>}
      {q.answered > 0 && q.type === 'rating' && (
        <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
          <Typography variant="h5" fontWeight={700}>{q.average}</Typography><Rating value={q.average} precision={0.1} readOnly /><Typography variant="body2" color="text.secondary">out of 5</Typography>
        </Stack>
      )}
      {q.answered > 0 && q.type === 'scale' && <Typography mb={1}>Average <b>{q.average}</b></Typography>}
      {q.answered > 0 && q.type === 'nps' && <NpsBar q={q} />}
      {q.answered > 0 && q.type === 'number' && <Typography>Average <b>{q.average}</b> · lowest {q.min} · highest {q.max}</Typography>}
      {q.answered > 0 && q.distribution && <Bars rows={q.type === 'rating' ? [...q.distribution].reverse().map(d => ({ ...d, label: `${d.label} ★` })) : q.distribution} />}
      {q.answered > 0 && q.answers && (
        <Stack spacing={1} sx={{ maxHeight: 280, overflow: 'auto' }}>
          {q.answers.map((a, i) => (
            <Box key={i} sx={{ borderLeft: '3px solid #E6D9C6', pl: 1.5 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{a.text}</Typography>
              <Typography variant="caption" color="text.secondary">{[a.by, a.about, fmt(a.at)].filter(Boolean).join(' · ')}</Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

function Invitations({ surveyId, onPerson }) {
  const [status, setStatus] = useState('');
  const { data, loading } = useQuery(SURVEY_INVITATIONS_QUERY, { variables: { id: surveyId, status: status || null, limit: 300 }, fetchPolicy: 'network-only' });
  const inv = data?.surveyInvitations;
  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
        <TextField select size="small" label="Status" value={status} onChange={e => setStatus(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">All</MenuItem>
          {['pending', 'held', 'sent', 'opened', 'completed', 'expired', 'failed'].map(st => <MenuItem key={st} value={st}>{st}</MenuItem>)}
        </TextField>
        <Typography variant="body2" color="text.secondary">{inv?.total ?? 0} invitation(s)</Typography>
      </Stack>
      {loading && <LinearProgress />}
      <Table size="small">
        <TableHead><TableRow><TableCell>Person</TableCell><TableCell>About</TableCell><TableCell>Status</TableCell><TableCell>Delivery</TableCell><TableCell>Link</TableCell></TableRow></TableHead>
        <TableBody>
          {(inv?.rows || []).map(i => (
            <TableRow key={i.id}>
              <TableCell><Link component="button" variant="body2" fontWeight={600} onClick={() => onPerson(i.userId)}>{i.name}</Link><Typography variant="caption" color="text.secondary" display="block">{i.email} · {i.roleLabel}</Typography></TableCell>
              <TableCell><Typography variant="body2">{i.subjectLabel || '—'}</Typography>{i.triggerDate && <Typography variant="caption" color="text.secondary">Date {i.triggerDate}</Typography>}</TableCell>
              <TableCell>
                <Chip size="small" label={i.status} color={STATUS_COLOR[i.status] || 'default'} />
                <Typography variant="caption" color="text.secondary" display="block">{i.completedAt ? `Answered ${fmt(i.completedAt)}${i.completedVia ? ` via ${VIA_LABEL[i.completedVia] || i.completedVia}` : ''}` : i.openedAt ? `Opened ${fmt(i.openedAt)}${i.openedVia ? ` via ${VIA_LABEL[i.openedVia] || i.openedVia}` : ''}` : i.sentAt ? `Sent ${fmt(i.sentAt)}` : ''}{i.remindersSent ? ` · ${i.remindersSent} reminder(s)` : ''}</Typography>
                {i.lastError && <Typography variant="caption" color="error" display="block">{i.lastError}</Typography>}
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {[...new Map(i.deliveries.map(d => [d.channel, d])).values()].map(d => (
                    d.status === 'manual' && d.error?.startsWith('https://wa.me/')
                      ? <Chip key={d.channel} size="small" variant="outlined" label="WhatsApp: send by hand" component="a" href={d.error} target="_blank" clickable />
                      : <Tooltip key={d.channel} title={d.error || ''}><Chip size="small" variant="outlined" label={`${d.channel.replace('_', '-')}: ${d.status}`} color={d.status === 'sent' ? 'success' : d.status === 'failed' ? 'error' : 'default'} /></Tooltip>
                  ))}
                </Stack>
              </TableCell>
              <TableCell><IconButton size="small" onClick={() => { try { navigator.clipboard.writeText(i.link); } catch (e) { /* ignore */ } }}><ContentCopyIcon fontSize="inherit" /></IconButton></TableCell>
            </TableRow>
          ))}
          {!loading && !inv?.rows?.length && <TableRow><TableCell colSpan={5} align="center">No invitations yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Box>
  );
}

export default function SurveyResults({ surveyId, onClose }) {
  const [tab, setTab] = useState(0);
  const [role, setRole] = useState('');
  const [open, setOpen] = useState(null);
  const [person, setPerson] = useState(null);
  const filter = role ? { role } : {};
  const { data, loading, error } = useQuery(SURVEY_RESULTS_QUERY, { variables: { id: surveyId, filter }, fetchPolicy: 'network-only' });
  const [getCsv, { loading: csvLoading }] = useLazyQuery(SURVEY_RESPONSES_CSV_QUERY, { fetchPolicy: 'network-only' });
  const r = data?.surveyResults;
  const sv = r?.survey;
  const qIndex = {}; let k = 0; (r?.questions || []).forEach(q => { qIndex[q.id] = ++k; });
  const download = async () => {
    const { data: d } = await getCsv({ variables: { id: surveyId, filter } });
    const url = URL.createObjectURL(new Blob(['﻿', d.surveyResponsesCsv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a'); a.href = url; a.download = `${(sv?.title || 'survey').replace(/[^\w-]+/g, '_')}-responses.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <Dialog open fullScreen onClose={onClose}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700}>{sv?.title || 'Results'}</Typography>
          {sv && <Typography variant="caption" color="text.secondary">{sv.triggerLabel}{sv.triggerType !== 'manual' && sv.triggerType !== 'scheduled' ? ` (${sv.triggerDelayDays} day${sv.triggerDelayDays === 1 ? '' : 's'})` : ''} · {sv.status}{sv.anonymous ? ' · anonymous' : ''}</Typography>}
        </Box>
        <TextField select size="small" label="Answered by" value={role} onChange={e => setRole(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">Everyone</MenuItem>
          {(sv?.audienceRoles || []).map(x => <MenuItem key={x} value={x}>{({ bride_family: 'Bride’s family', groom_family: 'Groom’s family', vendor: 'Vendor', matchmaker: 'Matchmaker' })[x] || x}</MenuItem>)}
        </TextField>
        <Button startIcon={<DownloadIcon />} disabled={csvLoading || !r?.summary?.responses} onClick={download}>Export CSV</Button>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#FBFAFC' }}>
        {loading && <LinearProgress />}
        {error && <Alert severity="error">{error.message}</Alert>}
        {r && (
          <>
            <Grid container spacing={2} mb={2}>
              <Grid item xs={6} md={2}><Tile label="Invited" value={r.summary.invited} sub={`${r.summary.reached} reached`} /></Grid>
              <Grid item xs={6} md={2}><Tile label="Responses" value={r.summary.responses} sub={`${r.summary.opened} opened the link`} /></Grid>
              <Grid item xs={6} md={2}><Tile label="Response rate" value={`${r.summary.responseRate}%`} sub="of those reached" /></Grid>
              <Grid item xs={6} md={2}><Tile label="Average rating" value={r.summary.ratingAvg ?? '—'} sub="out of 5 (rating questions)" /></Grid>
              <Grid item xs={6} md={2}><Tile label="NPS" value={r.summary.npsScore == null ? '—' : (r.summary.npsScore > 0 ? `+${r.summary.npsScore}` : r.summary.npsScore)} sub="−100 to +100" /></Grid>
              <Grid item xs={6} md={2}><Tile label="Held · expired · failed" value={`${r.summary.held} · ${r.summary.expired} · ${r.summary.failed}`} sub="held = messages switched off" /></Grid>
            </Grid>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label="Summary" /><Tab label={`Responses (${r.summary.responses})`} /><Tab label="Invitations" />
            </Tabs>
            {tab === 0 && (
              <Grid container spacing={2}>
                {r.bySubject.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2.5 }}>
                      <Typography fontWeight={700} mb={1}>By vendor / subject</Typography>
                      <Table size="small">
                        <TableHead><TableRow><TableCell>Name</TableCell><TableCell align="right">Responses</TableCell><TableCell align="right">Avg rating</TableCell></TableRow></TableHead>
                        <TableBody>{r.bySubject.map(x => <TableRow key={x.id}><TableCell>{x.name}</TableCell><TableCell align="right">{x.responses}</TableCell><TableCell align="right">{x.ratingAvg ?? '—'}</TableCell></TableRow>)}</TableBody>
                      </Table>
                    </Paper>
                  </Grid>
                )}
                {r.byRole.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2.5 }}>
                      <Typography fontWeight={700} mb={1}>By respondent group</Typography>
                      <Table size="small">
                        <TableHead><TableRow><TableCell>Group</TableCell><TableCell align="right">Responses</TableCell><TableCell align="right">Avg rating</TableCell></TableRow></TableHead>
                        <TableBody>{r.byRole.map(x => <TableRow key={x.role}><TableCell>{x.label}</TableCell><TableCell align="right">{x.responses}</TableCell><TableCell align="right">{x.ratingAvg ?? '—'}</TableCell></TableRow>)}</TableBody>
                      </Table>
                    </Paper>
                  </Grid>
                )}
                {r.questions.map(q => <Grid item xs={12} md={6} key={q.id}><QuestionCard q={q} index={qIndex[q.id]} /></Grid>)}
                {!r.summary.responses && <Grid item xs={12}><Alert severity="info">No responses yet. They appear here as people answer.</Alert></Grid>}
              </Grid>
            )}
            {tab === 1 && (
              <Paper variant="outlined">
                <Table size="small">
                  <TableHead><TableRow><TableCell>Submitted</TableCell><TableCell>By</TableCell><TableCell>About</TableCell><TableCell>Answered via</TableCell><TableCell align="right">Avg rating</TableCell><TableCell align="right">NPS</TableCell><TableCell /></TableRow></TableHead>
                  <TableBody>
                    {r.responses.map(x => (
                      <TableRow key={x.id} hover>
                        <TableCell>{fmt(x.submittedAt)}</TableCell>
                        <TableCell>{x.respondent || 'Anonymous'}<Typography variant="caption" color="text.secondary" display="block">{x.roleLabel}</Typography></TableCell>
                        <TableCell>{x.subjectLabel || '—'}</TableCell>
                        <TableCell>{x.channel ? <Chip size="small" variant="outlined" label={VIA_LABEL[x.channel] || x.channel} /> : '—'}</TableCell>
                        <TableCell align="right">{x.ratingAvg ?? '—'}</TableCell>
                        <TableCell align="right">{x.nps ?? '—'}</TableCell>
                        <TableCell><Link component="button" onClick={() => setOpen(x)}>View answers</Link></TableCell>
                      </TableRow>
                    ))}
                    {!r.responses.length && <TableRow><TableCell colSpan={7} align="center">No responses yet.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </Paper>
            )}
            {tab === 2 && <Paper variant="outlined" sx={{ p: 2 }}><Invitations surveyId={surveyId} onPerson={setPerson} /></Paper>}
          </>
        )}
      </DialogContent>
      {person && <PersonSurveys userId={person} onClose={() => setPerson(null)} />}
      {open && (
        <Dialog open onClose={() => setOpen(null)} maxWidth="sm" fullWidth>
          <DialogTitle>{open.respondent || 'Anonymous'} · {fmt(open.submittedAt)}{open.channel ? ` · ${VIA_LABEL[open.channel] || open.channel}` : ''}</DialogTitle>
          <DialogContent dividers>
            {(sv?.questions || []).filter(q => q.type !== 'section').map(q => {
              const a = open.answers?.[q.id];
              return (
                <Box key={q.id} mb={1.5}>
                  <Typography variant="body2" fontWeight={600}>{q.label}</Typography>
                  <Typography variant="body2" color={a == null || a === '' ? 'text.secondary' : 'text.primary'} sx={{ whiteSpace: 'pre-line' }}>
                    {a == null || a === '' ? 'Not answered' : Array.isArray(a) ? a.join(', ') : q.type === 'rating' ? `${a} ★` : q.type === 'yesno' ? (a === 'yes' ? 'Yes' : 'No') : String(a)}
                  </Typography>
                </Box>
              );
            })}
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}
